import {ModalBody, ModalFooter} from '@components/ui/modal-atoms'
import {Button} from '@components/ui/button'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {useDropzone} from 'react-dropzone'
import {FC, useEffect} from 'react'
import {usePhotosUpload} from '@features/photos-upload/store.ts'
import {toast} from '@utilities/toast'
import {Dropzone} from '@components/ui/dropzone'
import {AlertCircleIcon, AlertTriangleIcon, Image01Icon} from '@components/ui/icon'
import {Checkbox} from '@components/commons/checkbox'
import {cn, errorHandler, raise} from '@utilities/helpers.ts'
import {z} from 'zod'
import {Media} from '@features/photos-upload/types.ts'
import {
    httpBulkDeleteUploadedImages,
    httpGetAssignmentUploadedImages
} from '@features/photos-upload/http/photos-upload.http.ts'
import {useAsync} from '@hooks/useAsync.ts'
import {FilesList} from '@features/photos-upload/components/files-list'
import {IdleStepFileRow, IdleStepFileRowSkeleton} from '@features/photos-upload/components/idle-step-file-row'
import {ErrorBox} from '@components/commons/error-box'
import {ScrollArea, Scrollbar} from '@components/ui/scroll-area'
import {PhotosUploadModalWrapper} from '@features/photos-upload/components/photos-upload-modal-wrapper'
import {Spinner} from '@components/ui/spinner'

export const IdleStep: FC<{initialMaxFiles: number}> = ({initialMaxFiles}) => {
    const {t} = useTranslation()
    const getAssignmentUploadedImages = useAsync()
    const deleteAllImages = useAsync()
    const assignmentId = usePhotosUpload(store => store.assignmentId)
    const setIsOpen = usePhotosUpload(store => store.setIsOpen)
    const setStep = usePhotosUpload(store => store.setStep)
    const dropzoneFiles = usePhotosUpload(store => store.dropzoneFiles)
    const selectDropzoneFiles = usePhotosUpload(store => store.selectDropzoneFiles)
    const unselectDropzoneFiles = usePhotosUpload(store => store.unselectDropzoneFiles)
    const selectedDropzoneFiles = usePhotosUpload(store => store.selectedDropzoneFiles)
    const removeDropzoneFiles = usePhotosUpload(store => store.removeDropzoneFiles)
    const uploadedFiles = usePhotosUpload(store => store.uploadedFiles)
    const setUploadedFiles = usePhotosUpload(store => store.setUploadedFiles)
    const selectUploadedFiles = usePhotosUpload(store => store.selectUploadedFiles)
    const unselectUploadedFiles = usePhotosUpload(store => store.unselectUploadedFiles)
    const selectedUploadedFiles = usePhotosUpload(store => store.selectedUploadedFiles)
    const removeUploadedFiles = usePhotosUpload(store => store.removeUploadedFiles)
    const addDropzoneFiles = usePhotosUpload(store => store.addDropzoneFiles)
    const maxFiles = usePhotosUpload(store => store.maxFiles)
    const setMaxFiles = usePhotosUpload(store => store.setMaxFiles)
    const dropzone = useDropzone({
        accept: {'image/x-adobe-dng': ['.dng']},
        multiple: true,
        maxFiles,
        onDropAccepted: addDropzoneFiles,
        onDropRejected: rejectedFiles => {
            if (rejectedFiles.length > maxFiles) {
                toast.error(t('single_shooting:upload_photo_limit_error', {maxFiles}))
            }
        }
    })

    const getUploadedFiles = async () => {
        try {
            const response = await getAssignmentUploadedImages.run(
                httpGetAssignmentUploadedImages({
                    urlParams: {assignmentId: assignmentId ?? raise('assignmentId is nullish')}
                })
            )
            const responseData = z.object({media: z.array(Media)}).parse(response.data)
            setUploadedFiles(responseData.media)
        } catch (error) {
            errorHandler(error)
            console.error(error)
        }
    }

    useEffect(() => {
        setMaxFiles(initialMaxFiles)
        void getUploadedFiles()
    }, [])

    const handleRemoveUpdatedFiles = async (files: number[]) => {
        try {
            await deleteAllImages.run(
                httpBulkDeleteUploadedImages({
                    urlParams: {assignmentId: assignmentId ?? raise('assignmentId is nullish')},
                    params: {ids: files}
                })
            )
            removeUploadedFiles(files)
            unselectUploadedFiles(selectedUploadedFiles)
        } catch (error) {
            errorHandler(error)
        }
    }

    return (
        <PhotosUploadModalWrapper>
            <ScrollArea scrollbar={<Scrollbar />}>
                <ModalBody>
                    <Dropzone state={dropzone}>
                        <div
                            className={cn(
                                'min-h-[142px] p-7 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer',
                                '[&_p]:overflow-x-hidden [&_p]:max-w-full [&_p]:whitespace-nowrap [&_p]:text-ellipsis [&_p]:text-black [&_p]:text-sm',
                                '[&_p_span]:underline [&_p_span]:underline-offset-2 [&_svg]:fill-neutral-500',
                                dropzone.isDragReject
                                    ? 'border-danger-600 bg-danger-50'
                                    : dropzone.isDragAccept
                                      ? 'border-success-600 bg-success-50'
                                      : 'border-neutral-300 bg-white'
                            )}
                        >
                            <Image01Icon size={24} />
                            <p>{t('single_shooting:drop_assets')}</p>
                            <p>
                                <Trans i18nKey="assignment:or_upload" components={{span: <span />}} />
                            </p>
                        </div>
                    </Dropzone>
                    <div className="flex items-center gap-1.5 text-sm py-1.5 px-2 font-normal bg-blue-50 border border-blue-300 rounded-2xl w-full [&_svg]:fill-blue-600">
                        <AlertCircleIcon size={24} />
                        <p>{t('single_shooting:dropzone_banner')}</p>
                    </div>
                    {(dropzoneFiles.length >= 1 || uploadedFiles.length >= 1) && (
                        <div className="flex justify-between items-center gap-6">
                            <Checkbox
                                onCheckedChange={checked => {
                                    const allUploadedFilesIds = uploadedFiles.map(uploadedFile => uploadedFile.id)
                                    const allUploadedFilesNames = dropzoneFiles.map(dropzoneFile => dropzoneFile.name)
                                    if (checked) {
                                        selectDropzoneFiles(allUploadedFilesNames)
                                        selectUploadedFiles(allUploadedFilesIds)
                                    } else {
                                        unselectDropzoneFiles(allUploadedFilesNames)
                                        unselectUploadedFiles(allUploadedFilesIds)
                                    }
                                }}
                                checked={
                                    (dropzoneFiles.length >= 1 || uploadedFiles.length >= 1) &&
                                    selectedDropzoneFiles.length == dropzoneFiles.length &&
                                    selectedUploadedFiles.length == uploadedFiles.length
                                }
                                id="select_all"
                                label={t('single_shooting:select_all')}
                                disabled={dropzoneFiles.length == 0 && uploadedFiles.length == 0}
                            />
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    removeDropzoneFiles(selectedDropzoneFiles)
                                    void handleRemoveUpdatedFiles(selectedUploadedFiles)
                                    unselectDropzoneFiles(selectedDropzoneFiles)
                                }}
                                disabled={
                                    deleteAllImages.isLoading ||
                                    (selectedDropzoneFiles.length == 0 && selectedUploadedFiles.length == 0)
                                }
                            >
                                {t('single_shooting:delete_selected')}
                                {deleteAllImages.isLoading && <Spinner />}
                            </Button>
                        </div>
                    )}
                    {dropzoneFiles.length >= 1 && (
                        <FilesList
                            title={`${t('single_shooting:to_be_uploaded')} (${dropzoneFiles.length})`}
                            data={dropzoneFiles}
                            dataItem={(_, file) => <IdleStepFileRow fileName={file.name} />}
                        />
                    )}
                    {getAssignmentUploadedImages.isLoading ? (
                        <FilesList
                            title=""
                            data={Array.from({length: 5})}
                            dataItem={() => <IdleStepFileRowSkeleton />}
                        />
                    ) : getAssignmentUploadedImages.isError ? (
                        <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
                    ) : uploadedFiles.length >= 1 ? (
                        <FilesList
                            title={`${t('single_shooting:already_uploaded')} (${uploadedFiles.length})`}
                            data={uploadedFiles}
                            dataItem={(_, file) => <IdleStepFileRow isUploaded fileId={file.id} fileName={file.name} />}
                        />
                    ) : (
                        <></>
                    )}
                </ModalBody>
            </ScrollArea>
            <ModalFooter>
                <Button variant="tertiary" onClick={() => setIsOpen(false)}>
                    {t('commons:cancel')}
                </Button>
                <Button
                    disabled={dropzoneFiles.length == 0 || getAssignmentUploadedImages.isLoading}
                    onClick={() => setStep('upload')}
                >
                    {t('single_shooting:confirm_upload')}
                </Button>
            </ModalFooter>
        </PhotosUploadModalWrapper>
    )
}
